import { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import { FaYoutube, FaInstagram } from 'react-icons/fa';
import { parseVideoUrl, ytEmbed, igEmbed } from '@/lib/video';
import { resolveMediaSrc } from '@/lib/api';

export const Lightbox = ({ item, onClose, onPrev, onNext }) => {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onPrev();
      if (e.key === 'ArrowRight') onNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, onPrev, onNext]);

  const video = item.type === 'video' ? parseVideoUrl(item.videoUrl) : null;
  const vertical = video?.isShort;

  return (
    <AnimatePresence>
      <motion.div
        data-testid="lightbox"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[70] bg-ink/95 backdrop-blur-xl flex items-center justify-center p-4 sm:p-8"
        onClick={onClose}
      >
        <button data-testid="lightbox-close" onClick={onClose} className="absolute top-4 right-4 sm:top-6 sm:right-6 text-white/70 hover:text-champagne transition-colors z-10 p-2" aria-label="Close">
          <X size={28} />
        </button>
        <button data-testid="lightbox-prev" onClick={(e) => { e.stopPropagation(); onPrev(); }} className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 text-white/50 hover:text-champagne transition-colors z-10 p-2" aria-label="Previous">
          <ChevronLeft size={32} />
        </button>
        <button data-testid="lightbox-next" onClick={(e) => { e.stopPropagation(); onNext(); }} className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 text-white/50 hover:text-champagne transition-colors z-10 p-2" aria-label="Next">
          <ChevronRight size={32} />
        </button>

        <motion.div
          key={item.id}
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4 }}
          className={`relative ${vertical ? 'h-[80vh] aspect-[9/16]' : 'w-full max-w-4xl'}`}
          onClick={(e) => e.stopPropagation()}
        >
          {item.type === 'image' && (
            <img src={resolveMediaSrc(item.image)} alt={`${item.title} — ${item.category} by Events by AJ`} className="w-full max-h-[75vh] object-contain" />
          )}

          {video?.platform === 'youtube' && (
            <div className={vertical ? 'w-full h-full' : 'w-full aspect-video'}>
              <iframe
                data-testid="lightbox-youtube-embed"
                src={ytEmbed(video.id)}
                title={item.title}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          )}

          {video?.platform === 'instagram' && video.id && (
            <div className="w-full h-full">
              <iframe
                data-testid="lightbox-instagram-embed"
                src={igEmbed(video)}
                title={item.title}
                className="w-full h-full bg-white"
                allowFullScreen
              />
            </div>
          )}

          {video?.platform === 'instagram' && !video.id && (
            <div className="w-full h-full flex flex-col items-center justify-center bg-surface border border-white/10 p-8 text-center">
              <FaInstagram size={40} className="text-champagne mb-6" />
              <p className="font-serif text-2xl text-cream mb-2">{item.title}</p>
              <p className="text-white/50 text-sm mb-8 max-w-xs">This reel can&rsquo;t be embedded here, but you can watch it on our Instagram.</p>
              <a data-testid="lightbox-instagram-link" href={item.videoUrl} target="_blank" rel="noopener noreferrer" className="btn-gold">
                Watch on Instagram <ExternalLink size={14} />
              </a>
            </div>
          )}

          <div className="mt-4 flex items-start justify-between gap-4">
            <div>
              <p className="caption-label">{item.category}{item.location ? ` — ${item.location}` : ''}</p>
              <p className="font-serif text-xl sm:text-2xl text-cream mt-1">{item.title}</p>
              {item.description && <p className="text-white/50 text-sm font-light mt-1 max-w-lg">{item.description}</p>}
            </div>
            {video?.platform === 'youtube' && (
              <a data-testid="lightbox-youtube-link" href={item.videoUrl} target="_blank" rel="noopener noreferrer" className="shrink-0 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-white/50 hover:text-champagne transition-colors">
                <FaYoutube size={16} /> YouTube
              </a>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
